'use client';

import ms from 'ms';
import { isEmpty } from 'remeda';
import { Loader2 } from 'lucide-react';
import { useDebounceValue, useOnClickOutside } from 'usehooks-ts';
import {
  useQuery,
  type DefinedInitialDataOptions,
} from '@tanstack/react-query';
import {
  useRef,
  useState,
  type JSX,
  type RefObject,
  type FocusEventHandler,
  type ChangeEventHandler,
} from 'react';

import { cn, formatAddress, wrapArray } from '~/utils';
import { Show } from '~/components/show';
import { Text } from '~/components/text';
import { Label } from '~/components/label';
import { Input } from '~/components/input';
import { HStack, Stack } from '~/components/stack';
import { locationApi, QK_SEARCH_LOCATIONS } from '~/requests/locationApi';
import { MapPinIcon, TrashIcon } from '~/components/icons';
import { LocationLabel } from '~/components/location-label';
import {
  Command,
  CommandItem,
  CommandList,
  CommandGroup,
} from '~/components/command';
import dompurify from 'dompurify';

import type { ILocationSearchVM } from '~/types';
import { useSearchQueryState } from '~/hooks/useSearchQueryState';

interface IGoogleMapStateProps {
  list: ILocationSearchVM[];
  cached: Record<string, boolean>;
}

interface IGoogleMapProps {
  isMulti?: boolean;
  placeholder?: string;
  value?: ILocationSearchVM | ILocationSearchVM[];
  onChange?: (value: ILocationSearchVM | ILocationSearchVM[]) => void;
  isSearchInput?: boolean;
}

export function GoogleMap({
  onChange,
  value = [],
  isMulti = true,
  isSearchInput = false,
  placeholder = '1 Martin Street St Leonards, Australia',
}: IGoogleMapProps): JSX.Element {
  const { address } = useSearchQueryState();
  const [inputValue, setInputValue] = useState(address ?? '');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [selected, setSelected] = useState<IGoogleMapStateProps>(() => {
    const val = isEmpty(value ?? undefined)
      ? ([] as ILocationSearchVM[])
      : wrapArray(value);

    return {
      list: val,
      cached: val.reduce<Record<string, boolean>>((result, v) => {
        result[v.name] = true;

        return result;
      }, {}),
    };
  });
  const [debouncedInputValue] = useDebounceValue(inputValue, ms('0.4s'));
  const {
    data: locations,
    isFetched,
    isPending,
  } = useQuery(getLocationQueryOptions(debouncedInputValue));

  useOnClickOutside(containerRef as RefObject<HTMLDivElement>, () => {
    setOpen(false);
  });

  const changeValues = async (values: ILocationSearchVM[]) => {
    if (onChange) {
      if (isMulti) {
        const newValues = await Promise.all(
          values.map(async (value) => {
            if (value.latitude && value.longitude) {
              return value;
            }
            const coordinate = await locationApi.fetchCoordinatesByPlaceId({
              placeId: value.placeId,
            });
            return {
              ...value,
              latitude: coordinate.latitude,
              longitude: coordinate.longitude,
              placeId: value.placeId,
            };
          }),
        );
        onChange(newValues);
      } else {
        const coordinate = await locationApi.fetchCoordinatesByPlaceId({
          placeId: values[0].placeId,
        });
        onChange({
          ...values[0],
          latitude: coordinate.latitude,
          longitude: coordinate.longitude,
        });
      }
    }
  };

  const handleOnChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    const { value } = e.target;
    setInputValue(value);
    if (!value) {
      if (!isMulti) {
        onChange?.({
          longitude: '',
          latitude: '',
          country: '',
          address: '',
          adrFormatAddress: '',
          name: '',
          placeId: '',
          city: '',
        });
      }
      return;
    }
  };

  const handleOnFocus: FocusEventHandler<HTMLInputElement> = () => {
    setOpen(true);
  };

  const handleSelect = (location: ILocationSearchVM) => {
    setOpen(false);

    const { list, cached } = selected;
    let newList = structuredClone(list);

    if (isSearchInput) {
      setInputValue(formatAddress(location));
    } else {
      setInputValue('');
    }

    if (inputRef.current) {
      inputRef.current.value = '';
    }

    if (cached[location.name]) {
      // Location is already cached, but we still want to update it
      if (isMulti) {
        // For multi-select, find and update the existing location in the list
        const existingIndex = newList.findIndex(
          (l) => l.name === location.name,
        );
        if (existingIndex !== -1) {
          newList[existingIndex] = location;
        } else {
          newList.push(location);
        }
      } else {
        newList = [location];
      }
    } else {
      if (isMulti) {
        newList.push(location);
      } else {
        newList = [location];
      }
    }

    changeValues(newList);
    setSelected({
      list: newList,
      cached: {
        ...cached,
        [location.name]: true,
      },
    });
  };

  const handleRemove = (location: ILocationSearchVM) => {
    const newList = selected.list.filter((l) => {
      if (l.id) {
        return l.id !== location.id;
      }

      return l.name !== location.name;
    });

    changeValues(newList);
    setSelected({
      list: newList,
      cached: {
        ...selected.cached,
        [location.name]: false,
      },
    });
  };

  return (
    <Stack>
      <div className='relative' ref={containerRef}>
        <Label className='absolute left-4 top-1/2 -translate-y-1/2 [--svg-color:var(--color-blueBonnet-500)]'>
          <MapPinIcon className={cn('size-4', isSearchInput && 'hidden')} />
        </Label>
        <Input
          value={isSearchInput ? inputValue : undefined}
          ref={inputRef}
          className={cn(
            'pl-10',
            isSearchInput &&
              'p-0 rounded-none border-0 shadow-none h-max placeholder:text-dimGray-500 text-sm leading-[130%]',
          )}
          onFocus={handleOnFocus}
          placeholder={placeholder}
          onChange={handleOnChange}
        />
        <Show when={open && (isPending || locations.length)}>
          <div
            className={cn(
              'absolute mt-1 w-full rounded-md border bg-popover shadow-md z-10',
            )}
          >
            <Command>
              <CommandList>
                <CommandGroup className='p-0'>
                  <Show when={isPending}>
                    <CommandItem className='items-center justify-center'>
                      <Loader2
                        className={cn(
                          'size-10 animate-spin text-dimGray-500',
                          isSearchInput && 'size-6',
                        )}
                      />
                    </CommandItem>
                  </Show>
                  <Show when={isFetched}>
                    {locations.map((location) => {
                      const { name, adrFormatAddress } = location;

                      return (
                        <CommandItem
                          key={name}
                          className='px-4 py-3'
                          onSelect={() => handleSelect(location)}
                        >
                          <Text
                            className='text-[16px] leading-[130%]'
                            dangerouslySetInnerHTML={{
                              __html: dompurify.sanitize(adrFormatAddress),
                            }}
                          />
                        </CommandItem>
                      );
                    })}
                  </Show>
                </CommandGroup>
              </CommandList>
            </Command>
          </div>
        </Show>
      </div>
      <Show when={selected.list.length && !isSearchInput}>
        <Stack>
          {selected.list.map((address) => {
            const { id, name } = address;

            return (
              <HStack key={id || name} className='items-center' gap={2}>
                <LocationLabel location={address} className='flex-1' />
                <span
                  role='button'
                  className='[--svg-color:var(--color-desire-500)] cursor-pointer'
                  onClick={() => {
                    handleRemove(address);
                  }}
                >
                  <TrashIcon className='size-3.5' />
                </span>
              </HStack>
            );
          })}
        </Stack>
      </Show>
    </Stack>
  );
}

function getLocationQueryOptions(
  debouncedInputValue: string,
): DefinedInitialDataOptions<ILocationSearchVM[], Error, ILocationSearchVM[]> {
  return {
    queryKey: [QK_SEARCH_LOCATIONS, { search: debouncedInputValue }],
    queryFn: () => locationApi.searchLocations({ query: debouncedInputValue }),
    enabled: !!debouncedInputValue,
    initialData: [],
  };
}
